import type {
  BrandSpecials,
  MaterializedMenuProduct,
} from '@iheartjane/data-access';
import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import { FLAGS } from '@iheartjane/feature-flags';
import type { CompareProduct } from '@iheartjane/menu';
import {
  MAX_COMPARE,
  ProductCard as SharedProductCard,
  useCompareActions,
  WeightSelectorModal,
} from '@iheartjane/menu';
import { useToast } from '@iheartjane/reefer';
import type { PriceId } from '@iheartjane/types';
import type { SyntheticEvent } from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Form, useFetcher, useSubmit } from 'react-router';

import type { CartActionResult } from '@jane/bloom/actions/cart.server';
import { BagTransferModal } from '@jane/bloom/components/bagTransferModal/bagTransferModal';
import { BloomLink } from '@jane/bloom/components/bloomLink';
import { ClientOnly } from '@jane/bloom/components/clientOnly';
import { useCompareBarVisibilityActions } from '@jane/bloom/components/compareProducts/compareBarVisibility';
import { ProductCategoryLabel } from '@jane/bloom/components/productCategoryLabel/productCategoryLabel';
import { FavoriteSavedToastLabel } from '@jane/bloom/components/toast/favoriteSavedToastLabel';
import { useMarketplaceCart } from '@jane/bloom/hooks/cart/useMarketplaceCart';
import { useStoreSwitcher } from '@jane/bloom/hooks/storeSwitcher/useStoreSwitcher';
import { useAddToCartConfig } from '@jane/bloom/hooks/useAddToCartConfig';
import { useOptimisticBagSize } from '@jane/bloom/hooks/useBagSize';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useBrandSpecialShortfallNotice } from '@jane/bloom/providers/brandSpecialShortfallProvider';
import { CartAction } from '@jane/bloom/routes/api.cart-items/types';
import {
  trackAddedProductToCompare,
  trackProductListingTap,
} from '@jane/bloom/services/tracking/track';
import { mergeCartItem } from '@jane/bloom/utils/cart/mergeCartItem';
import { getMaxCartQuantity } from '@jane/bloom/utils/getMaxCartQuantity';
import type { ShouldShowWeightFilter } from '@jane/bloom/utils/specials/shouldShowWeight';
import { formatWeight } from '@jane/bloom/utils/strings/formatWeight';
import {
  buildProductCardProps,
  getDefaultWeight,
} from '@jane/bloom/viewModels/buildProductCardProps';
import { buildWeightOptions } from '@jane/bloom/viewModels/buildWeightOptions';
import { buildCompareProduct } from '@jane/bloom/viewModels/compareProduct';
import type { ProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearch';

import { CompareMenuItem } from './compareMenuItem';
import { ProductCardImageAction } from './productCardImageAction';

import styles from './productCard.module.css';

const COMPARE_MENU_CLOSE_DELAY_MS = 400;

export type ProductCardProps = {
  brandSpecials: BrandSpecials;
  customRowId?: number;
  isPlacement?: boolean;
  isSponsored?: boolean;
  maxWeightOption?: number | null;
  preloaded?: boolean;
  product: ProductSearchResultViewModel;
  selected?: () => void;
  smartProductInstance?: SmartSortProduct<MaterializedMenuProduct>;
  weightFilter?: ShouldShowWeightFilter;
};

export const ProductCard = forwardRef<HTMLDivElement, ProductCardProps>(
  function ProductCard(
    {
      brandSpecials,
      customRowId,
      isPlacement,
      isSponsored,
      maxWeightOption,
      preloaded,
      product,
      selected,
      smartProductInstance,
      weightFilter,
    },
    ref
  ) {
    const { pinnedStore, storeType } = useStoreSwitcher();
    const { cart, featureFlags, shoppingPreferences, theme } =
      useRootLoaderData();
    const submit = useSubmit();
    const toast = useToast();

    const defaultWeight = getDefaultWeight(product);
    const {
      fetcher: { state },
      formArgs,
    } = useAddToCartConfig({
      product,
      productId: product.product_id,
      quantity: 1,
      rowId: customRowId,
      selectedWeight: defaultWeight,
    });
    const { createCart, deleteCart, newCart } = useMarketplaceCart();

    const decreaseFetcher = useFetcher<CartActionResult>();
    useBrandSpecialShortfallNotice(decreaseFetcher.data);
    const isUpdatingCart =
      state === 'submitting' || decreaseFetcher.state !== 'idle';
    const countInCart = useOptimisticBagSize(product.product_id);

    const [showWarningModal, setShowWarningModal] = useState(false);
    const [isWeightModalOpen, setIsWeightModalOpen] = useState(false);

    const onSdkProductClick = useCallback(
      (addToCart = false) => {
        smartProductInstance?.click({ isAddToCart: addToCart });
      },
      [smartProductInstance]
    );

    const weights = useMemo(
      () =>
        buildWeightOptions({
          cart,
          maxWeightOption,
          product,
          smartProductInstance,
          storeState: pinnedStore?.state,
          weightFilter,
        }),
      [
        cart,
        maxWeightOption,
        pinnedStore?.state,
        product,
        smartProductInstance,
        weightFilter,
      ]
    );

    const handleIncrease = useCallback(() => {
      onSdkProductClick(true);
      submit(
        { count: 1, priceId: defaultWeight, productId: product.id },
        formArgs
      );
    }, [onSdkProductClick, submit, defaultWeight, product.id, formArgs]);

    const handleDecrease = useCallback(() => {
      decreaseFetcher.submit(
        {
          intent: CartAction.DeleteItem,
          productId: product.id?.toString() ?? null,
        },
        {
          action: `/api/cart-items`,
          method: 'POST',
        }
      );
    }, [decreaseFetcher, product.id]);

    const disabledWeights = useMemo(() => {
      const disabled = new Set<PriceId>();
      for (const weight of weights) {
        const remaining = getMaxCartQuantity({
          cart,
          currentProduct: product,
          currentWeight: weight.value,
          storeId: shoppingPreferences?.storeId,
        });
        if (remaining === 0) {
          disabled.add(weight.value);
        }
      }
      return disabled;
    }, [weights, cart, product, shoppingPreferences?.storeId]);

    const atMaxQuantity = product.hasMultipleWeights
      ? weights.length > 0 && disabledWeights.size === weights.length
      : getMaxCartQuantity({
          cart,
          currentProduct: product,
          currentWeight: defaultWeight,
          storeId: shoppingPreferences?.storeId,
        }) === 0;

    const cardProps = useMemo(
      () =>
        buildProductCardProps({
          atMaxQuantity,
          brandSpecials,
          cartCount: countInCart,
          isPlacement,
          isSponsored,
          isUpdatingCart,
          preloaded,
          product,
          productReviewsEnabled: pinnedStore?.product_reviews_enabled,
          storeState: pinnedStore?.state,
        }),
      [
        atMaxQuantity,
        brandSpecials,
        countInCart,
        isPlacement,
        isSponsored,
        isUpdatingCart,
        pinnedStore?.state,
        pinnedStore?.product_reviews_enabled,
        preloaded,
        product,
      ]
    );

    // if cart items in the cart are from a different from the current store, prevent the add to cart action and show warning modal
    const hasPreviousStoreProducts = cart.storeId
      ? cart.storeId !== shoppingPreferences?.storeId
      : false;

    const stageBagTransfer = useCallback(
      (items: Array<{ count: number; priceId: string; productId: number }>) => {
        if (shoppingPreferences) {
          let products = cart.items.map((item) => ({
            count: item.count,
            price_id: item.priceId,
            product_id: Number(item.productId),
          }));
          for (const item of items) {
            products = mergeCartItem(
              {
                count: item.count,
                price_id: formatWeight(item.priceId),
                product_id: item.productId,
              },
              products
            );
          }
          createCart({
            products,
            reservationMode: shoppingPreferences.reservationMode,
            storeId: shoppingPreferences.storeId,
          });
        }

        setShowWarningModal(true);
      },
      [shoppingPreferences, createCart, cart.items]
    );

    const handleWeightSelectorSubmit = useCallback(
      (selectedQuantities: Record<string, number>) => {
        onSdkProductClick(true);

        // Build items array with only changed weights
        const items = weights
          .filter((w) => selectedQuantities[w.value] !== w.count)
          .map((w) => ({
            count: selectedQuantities[w.value],
            priceId: w.value,
            productId: product.id,
          }));

        const countDelta = items.reduce((sum, item) => {
          const oldCount =
            weights.find((w) => w.value === item.priceId)?.count ?? 0;
          return sum + (item.count - oldCount);
        }, 0);

        if (items.length === 0) {
          setIsWeightModalOpen(false);
          return;
        }

        if (hasPreviousStoreProducts) {
          setIsWeightModalOpen(false);
          stageBagTransfer(items);
          return;
        }

        submit(
          {
            countDelta: countDelta.toString(),
            intent: 'updateMultipleWeights',
            items: JSON.stringify(items),
          },
          formArgs
        );
        setIsWeightModalOpen(false);
      },
      [
        onSdkProductClick,
        weights,
        product.id,
        hasPreviousStoreProducts,
        stageBagTransfer,
        submit,
        formArgs,
      ]
    );

    const handleSingleWeightAddToBag = (
      event: SyntheticEvent<HTMLFormElement, SubmitEvent>
    ) => {
      onSdkProductClick(true);

      if (hasPreviousStoreProducts) {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);

        const priceId = formData.get('priceId');
        stageBagTransfer([
          {
            count: Number(formData.get('count')),
            priceId: priceId ? String(priceId) : 'each',
            productId: Number(formData.get('productId')),
          },
        ]);
      }
    };

    const confirmStartNewBag = () => {
      setShowWarningModal(false);

      // replace the current cart with the new cart
      if (newCart) {
        submit({ shouldReplaceCart: 'true' }, formArgs);
      }
    };

    const handleCartWarningClose = () => {
      setShowWarningModal(false);

      // delete the pending cart
      if (newCart) {
        deleteCart({ cartUuid: newCart?.cart.uuid });
      }
    };

    // --- Compare products ---
    const compareMenuCloseTimer = useRef<
      ReturnType<typeof setTimeout> | undefined
    >(undefined);
    useEffect(() => () => clearTimeout(compareMenuCloseTimer.current), []);

    const compareEnabled = featureFlags[FLAGS.c1CompareProducts];
    const { getIsSelected, getProductCount, toggleProduct } =
      useCompareActions();
    const { revealBar: revealCompareBar } = useCompareBarVisibilityActions();

    const compareProduct = useMemo<CompareProduct | null>(() => {
      if (!compareEnabled) return null;
      return buildCompareProduct({
        brandSpecials,
        product,
        smartProductInstance,
        storeState: pinnedStore?.state,
      });
    }, [
      brandSpecials,
      compareEnabled,
      product,
      pinnedStore?.state,
      smartProductInstance,
    ]);

    const handleCompareToggle = useCallback(() => {
      if (!compareProduct) return;

      const isAdding = !getIsSelected(product.product_id);
      const success = toggleProduct(compareProduct);
      if (!success) {
        toast.add({
          label: `Maximum ${MAX_COMPARE} products for comparison`,
          time: 2500,
        });
      } else if (isAdding) {
        // Only matters on mobile PDP - otherwise, the Compare bar is always visible.
        revealCompareBar();
        trackAddedProductToCompare(getProductCount() + 1, {
          productBrand: product.brand,
          productId: product.product_id,
          productKind: product.kind,
          productName: product.name,
        });
      }
    }, [
      compareProduct,
      revealCompareBar,
      toggleProduct,
      getIsSelected,
      getProductCount,
      toast,
      product,
    ]);

    // --- Favorites ---
    const favoritesEnabled = Boolean(featureFlags[FLAGS.c2ProductFavorites]);
    // TODO: Local placeholder until the heart reads is_favorite and writes through the toggle action
    const [isFavorite, setIsFavorite] = useState(false);

    const handleFavoriteToggle = useCallback(() => {
      if (!isFavorite) {
        toast.add({ label: <FavoriteSavedToastLabel /> });
      }
      setIsFavorite(!isFavorite);
    }, [isFavorite, toast]);

    // Tapping the product fires both DM (smart-sort) click tracking via
    // `selected` and the Mixpanel listing-tap event. Shared so the image and
    // the product-name links stay in sync.
    const handleProductTap = useCallback(() => {
      selected?.();
      trackProductListingTap(
        {
          product,
          reservationMode: shoppingPreferences?.reservationMode,
          storeId: pinnedStore?.id ?? shoppingPreferences?.storeId,
          storeType,
        },
        featureFlags
      );
    }, [
      selected,
      storeType,
      product,
      shoppingPreferences?.reservationMode,
      shoppingPreferences?.storeId,
      pinnedStore?.id,
      featureFlags,
    ]);

    const pdpPath = `/products/${smartProductInstance?.attributes.product_id ?? product.product_id}/${product.unique_slug ?? product.id}`;

    return (
      <>
        <BagTransferModal
          open={showWarningModal}
          onConfirm={confirmStartNewBag}
          onRequestClose={handleCartWarningClose}
          newBag={newCart}
        />

        {product.hasMultipleWeights && (
          <ClientOnly>
            {() => (
              <WeightSelectorModal
                imageUrl={product.image_urls?.[0]}
                isUpdatingCart={isUpdatingCart}
                onRequestClose={() => setIsWeightModalOpen(false)}
                onSubmit={handleWeightSelectorSubmit}
                open={isWeightModalOpen}
                productName={product.name}
                weights={weights}
              />
            )}
          </ClientOnly>
        )}

        <SharedProductCard
          ref={ref}
          {...cardProps}
          cardBorder={theme.bloom?.product_cards?.card_border}
          cardSize={theme.bloom?.product_cards?.card_size}
          ctaIcon={theme.bloom?.product_cards?.cta_icon}
          ctaStyle={theme.bloom?.product_cards?.cta_style}
          hover={theme.bloom?.product_cards?.hover}
          cardMenuItems={
            compareProduct
              ? [
                  {
                    key: 'compare',
                    render: ({ closeMenu }) => (
                      <CompareMenuItem
                        onToggle={() => {
                          handleCompareToggle();
                          clearTimeout(compareMenuCloseTimer.current);
                          compareMenuCloseTimer.current = setTimeout(
                            closeMenu,
                            COMPARE_MENU_CLOSE_DELAY_MS
                          );
                        }}
                        productId={product.product_id}
                        productName={product.name}
                      />
                    ),
                  },
                ]
              : []
          }
          renderImageAction={
            compareProduct || favoritesEnabled
              ? ({ focusCardMenu }) => (
                  <ProductCardImageAction
                    compareEnabled={Boolean(compareProduct)}
                    favoritesEnabled={favoritesEnabled}
                    focusCardMenu={focusCardMenu}
                    isFavorite={isFavorite}
                    onCompareToggle={handleCompareToggle}
                    onFavoriteToggle={handleFavoriteToggle}
                    productId={product.product_id}
                    productName={product.name}
                  />
                )
              : undefined
          }
          onDecrease={handleDecrease}
          onIncrease={handleIncrease}
          onImpress={() => smartProductInstance?.impress()}
          onProductClick={handleProductTap}
          onSelectWeight={() => {
            onSdkProductClick(false);
            setIsWeightModalOpen(true);
          }}
          renderLink={({
            'aria-hidden': ariaHidden,
            children,
            className,
            onClick,
            tabIndex,
          }) => (
            <BloomLink
              to={pdpPath}
              aria-hidden={ariaHidden}
              className={className}
              onClick={onClick}
              style={className ? undefined : { textDecoration: 'none' }}
              tabIndex={tabIndex}
            >
              {children}
            </BloomLink>
          )}
          renderProductCategoryLabel={({ className }) => (
            <ProductCategoryLabel
              category={product.category}
              className={className}
              kind={product.kind}
            />
          )}
          renderAddToBag={(button) => (
            <Form
              name={`${product.id}-add-to-cart`}
              className={styles['productCardAddToCartForm']}
              onSubmit={handleSingleWeightAddToBag}
              {...formArgs}
            >
              <input type="hidden" name="productId" value={product.id} />
              <input type="hidden" name="priceId" value={defaultWeight} />
              <input type="hidden" name="count" value={1} />
              {button}
            </Form>
          )}
        />
      </>
    );
  }
);
